import {inject, Injectable} from '@angular/core';
import {map, Observable, timeout} from 'rxjs';
import {CommunicationService} from '../../http/communication.service';
import {ISO_DATE_ONLY_REGEX, toPolarisOrigin} from './polaris.utils';
import {
  PolarisAccountKey,
  PolarisApiChatMessagesResponseDto,
  PolarisApiChatsOverviewResponseDto,
  PolarisApiChatUploadDto,
  PolarisApiGraphDto,
  PolarisApiMessageDto,
  PolarisApiMessageRequestDto,
  PolarisApiSignalDto,
  PolarisApiWidgetContextDto,
  PolarisApiMessageUploadDto,
  PolarisApiSendMessageRequestDto,
  PolarisApiSendMessageResponseDto,
  PolarisApiUploadChatsRequestDto,
  PolarisApiUploadChatsResponseDto,
  PolarisChatId,
  PolarisChatByIdResult,
  PolarisChatAggregate,
  PolarisChatOverview,
  PolarisSendMessageInput,
  PolarisSendMessageResult,
  PolarisGraph,
  PolarisGraphSeries,
  PolarisMessagePair,
  PolarisWidgetContext,
  PolarisTurn,
  PolarisTurnStatus,
  PolarisPaginationParams,
  PolarisChatsOverviewResult
} from './model';

@Injectable({
  providedIn: 'root'
})
export class PolarisApiService {
  private communicationService = inject(CommunicationService);

  public getChatsOverview(
    accountKey: PolarisAccountKey,
    pagination: PolarisPaginationParams = {}
  ): Observable<PolarisChatsOverviewResult> {
    const path = this.getChatsOverviewPath(accountKey);
    const params = {
      limit: pagination.limit ?? 50,
      offset: pagination.offset ?? 0
    };
    return this.communicationService
      .httpGet<PolarisApiChatsOverviewResponseDto>(path, {params})
      .pipe(map(dto => this.mapChatsOverviewResponse(dto)));
  }

  public getChatById(
    accountKey: PolarisAccountKey,
    chatId: PolarisChatId,
    pagination: PolarisPaginationParams = {}
  ): Observable<PolarisChatByIdResult> {
    const path = this.getChatByIdPath(accountKey, chatId);
    const params = {
      all: pagination.all ?? true,
      limit: pagination.limit ?? 50,
      offset: pagination.offset ?? 0
    };
    return this.communicationService
      .httpGet<PolarisApiChatMessagesResponseDto>(path, {params})
      .pipe(map(dto => this.mapChatByIdResponse(dto)));
  }

  public sendMessage(request: PolarisSendMessageInput): Observable<PolarisSendMessageResult> {
    const dtoRequest = this.toApiSendMessageRequest(request);
    return this.communicationService
      .httpPost<PolarisApiSendMessageResponseDto>('/polaris/v2/chat/message', dtoRequest)
      .pipe(timeout(600000), map(dto => this.mapSendMessageResponse(dto, request)));
  }

  public uploadChats(
    accountKey: PolarisAccountKey,
    chats: PolarisChatAggregate[]
  ): Observable<PolarisChatAggregate | null> {
    const body: PolarisApiUploadChatsRequestDto = {
      chats: chats.map(chat => this.toApiChatUpload(chat))
    };
    const path = this.getUploadChatsPath(accountKey);

    return this.communicationService
      .httpPost<PolarisApiUploadChatsResponseDto | null>(path, body)
      .pipe(map(() => null));
  }

  private mapChatsOverviewResponse(dto: PolarisApiChatsOverviewResponseDto): PolarisChatsOverviewResult {
    return {
      items: dto.items.map(item => this.toChatOverview(item)),
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset
    };
  }

  private mapChatByIdResponse(dto: PolarisApiChatMessagesResponseDto): PolarisChatByIdResult {
    return {
      chat: this.toChatAggregate(dto.chat, dto.messages),
      pagination: dto.pagination
    };
  }

  private mapSendMessageResponse(
    dto: PolarisApiSendMessageResponseDto,
    request: PolarisSendMessageInput
  ): PolarisSendMessageResult {
    const chat = this.toChatOverview(dto.chat);
    const serverWidgetContext = this.toDomainWidgetContext(dto.message.widgetContext);
    const effectiveWidgetContext = serverWidgetContext ?? request.message.widgetContext;
    const messagePair = this.toMessagePair(dto.message, dto.chat.chatId, {
      userPrompt: request.message.userPrompt,
      userTimestamp: request.message.timestamp
    });
    const turn: PolarisTurn = {
      id: messagePair.interactionId ?? messagePair.id,
      chatId: dto.chat.chatId,
      status: this.toTurnStatus(this.getLegacyStatus(dto.message)),
      origin: effectiveWidgetContext?.origin,
      widgetName: effectiveWidgetContext?.name,
      widgetContext: effectiveWidgetContext,
      myBrand: dto.message.brandKey ?? request.message.brandKey,
      myCompetitors: this.toDomainCompetitorsKeys(dto.message.competitorsKeys) ?? request.message.competitorsKeys,
      wizard: request.message.wizard,
      alert: request.message.alert,
      messagePair
    };

    return {chat, turn};
  }

  private toChatAggregate(chat: PolarisApiChatMessagesResponseDto['chat'], messages: PolarisApiMessageDto[]): PolarisChatAggregate {
    const overview = this.toChatOverview(chat);
    const turns = messages.map(message => {
      const widgetContext = this.toDomainWidgetContext(message.widgetContext);
      const messagePair = this.toMessagePair(message, overview.chatId);
      return {
        id: messagePair.interactionId ?? messagePair.id,
        chatId: overview.chatId,
        status: this.toTurnStatus(this.getLegacyStatus(message)),
        origin: widgetContext?.origin,
        widgetName: widgetContext?.name,
        widgetContext,
        myBrand: message.brandKey,
        myCompetitors: this.toDomainCompetitorsKeys(message.competitorsKeys),
        messagePair
      };
    });
    return {
      overview,
      turns,
      isThinking: turns.some(turn => turn.status === 'pending')
    };
  }

  private toChatOverview(dto: PolarisApiChatMessagesResponseDto['chat']): PolarisChatOverview {
    return {
      chatId: dto.chatId,
      accountKey: dto.accountKey,
      userId: dto.userId,
      title: dto.title ?? 'New Chat',
      parentChatId: dto.parentChatId,
      createdAt: dto.createdAt,
      lastModified: dto.lastModified,
      lastMessageTime: dto.lastMessageTime
    };
  }

  private toMessagePair(
    dto: PolarisApiMessageDto,
    chatId: PolarisChatId,
    fallback: {userPrompt?: string; userTimestamp?: number} = {}
  ): PolarisMessagePair {
    const resolvedUserPrompt = dto.userPrompt ?? fallback.userPrompt ?? '';
    const resolvedResponse = dto.response ?? '';
    const resolvedUserTimestamp = this.resolveUserTimestamp(dto, fallback.userTimestamp);
    const resolvedSystemTimestamp = this.resolveSystemTimestamp(dto);
    return {
      id: dto.id,
      chatId,
      userPrompt: resolvedUserPrompt,
      response: resolvedResponse,
      status: this.getLegacyStatus(dto) ?? 'completed',
      userTimestamp: resolvedUserTimestamp,
      systemTimestamp: resolvedSystemTimestamp,
      followUpQuestions: this.toStringArray(dto.followUpQuestions),
      insights: this.toInsights(dto.insights),
      creativesURLs: this.toStringArray(dto.creativesURLs),
      graphs: (dto.graphs ?? []).map(graph => this.toDomainGraph(graph)),
      originalResponse: dto.originalResponse,
      polarisLambdaRequestId: this.getLegacyPolarisLambdaRequestId(dto)
    };
  }

  private toDomainGraph(dto: PolarisApiGraphDto): PolarisGraph {
    return {
      title: dto.title,
      type: dto.type,
      url: dto.url,
      x: dto.x,
      series: (dto.series ?? []).map(series => this.toDomainSeries(series)),
      yIsPercentage: dto.y_is_percentage,
      xAxis: dto.x_axis,
      yAxis: dto.y_axis
    };
  }

  private toDomainSeries(dto: PolarisApiGraphDto['series'][number]): PolarisGraphSeries {
    return {
      name: dto.name,
      y: dto.y,
      unit: dto.unit
    };
  }

  private toApiSendMessageRequest(input: PolarisSendMessageInput): PolarisApiSendMessageRequestDto {
    const message: PolarisApiMessageRequestDto = {
      brandKey: input.message.brandKey,
      competitorsKeys: this.toApiCompetitorsKeys(input.message.competitorsKeys),
      widgetContext: this.toApiWidgetContext(input.message.widgetContext),
      userPrompt: input.message.userPrompt,
      timestamp: input.message.timestamp,
      conversationHistory: input.message.conversationHistory.map(item => ({
        role: item.role,
        content: item.content
      })),
      debug: input.message.debug ?? false,
      wizard: input.message.wizard,
      alert: input.message.alert,
      signal: input.message.signal ? this.toApiSignalPayload(input.message.signal) : undefined
    };

    return {
      chatId: input.chatId,
      createNewChat: input.createNewChat ?? false,
      accountKey: input.accountKey,
      message
    };
  }

  private toApiSignalPayload(
    signal: NonNullable<PolarisSendMessageInput['message']['signal']>
  ): PolarisApiSignalDto {
    return JSON.parse(JSON.stringify(signal)) as PolarisApiSignalDto;
  }

  private toApiWidgetContext(input: PolarisSendMessageInput['message']['widgetContext']): PolarisApiWidgetContextDto | undefined {
    if (!input) {
      return undefined;
    }
    const dateRange = this.toStrictDateRange(input.dateRange);
    if (!input.origin || !input.name || !input.myBrand || !input.device || !dateRange) {
      return undefined;
    }
    return {
      origin: input.origin,
      name: input.name,
      metrics: input.metrics ?? [],
      myBrand: input.myBrand,
      myCompetitors: input.myCompetitors ?? [],
      dateRange,
      countries: input.countries ?? [],
      device: input.device
    };
  }

  private toDomainWidgetContext(input: PolarisApiWidgetContextDto | undefined): PolarisTurn['widgetContext'] {
    if (!input) {
      return undefined;
    }
    return {
      origin: this.toDomainOrigin(input.origin),
      name: input.name,
      metrics: input.metrics,
      myBrand: input.myBrand,
      myCompetitors: input.myCompetitors,
      dateRange: this.toStrictDateRange(input.dateRange),
      countries: input.countries,
      device: input.device
    };
  }

  private toStrictDateRange(
    dateRange: PolarisApiWidgetContextDto['dateRange'] | NonNullable<PolarisSendMessageInput['message']['widgetContext']>['dateRange']
  ): {from: string; to: string} | undefined {
    if (!dateRange) {
      return undefined;
    }
    if (typeof dateRange === 'string') {
      throw new Error(`Invalid dateRange type. Expected { from: string; to: string }, got "${dateRange}".`);
    }

    this.assertIsoDateOnly(dateRange.from, 'from');
    this.assertIsoDateOnly(dateRange.to, 'to');

    return {
      from: dateRange.from,
      to: dateRange.to
    };
  }

  private assertIsoDateOnly(value: string, fieldName: 'from' | 'to'): void {
    if (!ISO_DATE_ONLY_REGEX.test(value)) {
      throw new Error(`Invalid dateRange.${fieldName} format. Expected YYYY-MM-DD, got "${value}".`);
    }

    const [yearPart, monthPart, dayPart] = value.split('-');
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);
    const parsedDate = new Date(Date.UTC(year, month - 1, day));

    if (
      Number.isNaN(parsedDate.getTime()) ||
      parsedDate.getUTCFullYear() !== year ||
      parsedDate.getUTCMonth() + 1 !== month ||
      parsedDate.getUTCDate() !== day
    ) {
      throw new Error(`Invalid dateRange.${fieldName} value. Expected a real calendar date in YYYY-MM-DD format, got "${value}".`);
    }
  }

  private toApiChatUpload(chat: PolarisChatAggregate): PolarisApiChatUploadDto {
    const messages: PolarisApiMessageUploadDto[] = chat.turns.map(turn => ({
      id: turn.messagePair.id,
      brandKey: turn.myBrand,
      competitorsKeys: turn.myCompetitors,
      userPrompt: turn.messagePair.userPrompt,
      response: turn.messagePair.response,
      userTimestamp: this.toUploadTimestamp(turn.messagePair.userTimestamp),
      author: this.toUploadAuthor(turn),
      widgetContext: this.toApiUploadWidgetContext(turn),
      insights: turn.messagePair.insights ?? undefined,
      graphs: turn.messagePair.graphs.length ? turn.messagePair.graphs.map(graph => ({
        title: graph.title,
        type: graph.type,
        url: graph.url,
        x: graph.x,
        series: graph.series.map(series => ({
          name: series.name,
          y: series.y,
          unit: series.unit
        })),
        y_is_percentage: graph.yIsPercentage,
        x_axis: graph.xAxis,
        y_axis: graph.yAxis
      })) : undefined,
      creativesURLs: turn.messagePair.creativesURLs,
      followUpQuestions: turn.messagePair.followUpQuestions
    }));

    return {
      chatId: chat.overview.chatId,
      title: chat.overview.title,
      lastUpdated: this.toUploadTimestamp(chat.overview.lastModified ?? chat.overview.lastMessageTime ?? chat.overview.createdAt),
      messages
    };
  }

  private toUploadAuthor(turn: PolarisTurn): PolarisApiMessageUploadDto['author'] {
    const hasAssistantPayload = Boolean(
      turn.messagePair.response.trim() ||
      turn.messagePair.followUpQuestions.length ||
      turn.messagePair.creativesURLs.length ||
      turn.messagePair.graphs.length ||
      (turn.messagePair.insights?.length ?? 0) > 0
    );
    return hasAssistantPayload ? 'assistant' : 'user';
  }

  private toApiUploadWidgetContext(turn: PolarisTurn): PolarisApiWidgetContextDto | undefined {
    const widgetContext = turn.widgetContext;
    if (!widgetContext) {
      return undefined;
    }

    const origin = widgetContext.origin ?? turn.origin;
    const name = widgetContext.name ?? turn.widgetName;
    const myBrand = widgetContext.myBrand ?? turn.myBrand;
    const myCompetitors = widgetContext.myCompetitors ?? turn.myCompetitors ?? [];
    const dateRange = this.safeToStrictDateRange(widgetContext.dateRange);

    if (!origin || !name || !myBrand || !widgetContext.device || !dateRange) {
      return undefined;
    }

    return {
      origin,
      name,
      metrics: widgetContext.metrics ?? [],
      myBrand,
      myCompetitors,
      dateRange,
      countries: widgetContext.countries ?? [],
      device: widgetContext.device
    };
  }

  private safeToStrictDateRange(dateRange: PolarisWidgetContext['dateRange']): {from: string; to: string} | undefined {
    try {
      return this.toStrictDateRange(dateRange);
    } catch {
      // Legacy widget context may have malformed date ranges; omit instead of failing migration upload.
      return undefined;
    }
  }

  private toUploadTimestamp(value: number | string | undefined): number {
    if (typeof value === 'number' && value > 0 && !Number.isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return Date.now();
  }

  private toTurnStatus(status?: string): PolarisTurnStatus {
    const normalized = (status ?? '').toLowerCase();
    switch (normalized) {
      case 'success':
        return 'completed';
      case 'error':
        return 'failed';
      case 'clarification_needed':
        return 'completed';
      case 'in_progress':
        return 'pending';
      default:
        return 'completed';
    }
  }

  private getChatsOverviewPath(accountKey: PolarisAccountKey): string {
    return `/polaris/v2/accounts/${accountKey}/chats`;
  }

  private getChatByIdPath(accountKey: PolarisAccountKey, chatId: PolarisChatId): string {
    return `/polaris/v2/accounts/${accountKey}/chats/${chatId}`;
  }

  private getUploadChatsPath(accountKey: PolarisAccountKey): string {
    return `/polaris/v2/accounts/${accountKey}/chats`;
  }

  private resolveUserTimestamp(dto: PolarisApiMessageDto, fallbackTimestamp?: number): number {
    const legacyTimestamp = this.getLegacyTimestamp(dto);
    const defaultTimestamp = this.normalizeTimestamp(fallbackTimestamp, Date.now());
    const fallback = this.normalizeTimestamp(legacyTimestamp, defaultTimestamp);
    return this.normalizeTimestamp(dto.userTimestamp, fallback);
  }

  private resolveSystemTimestamp(dto: PolarisApiMessageDto): number | null {
    const normalizedAuthor = dto.author?.toLowerCase();
    const candidate = normalizedAuthor === 'assistant' ? dto.userTimestamp : this.getLegacyTimestamp(dto);
    return this.normalizeTimestamp(candidate, null);
  }

  private getLegacyTimestamp(dto: PolarisApiMessageDto): number | undefined {
    return (dto as PolarisApiMessageDto & {timestamp?: number}).timestamp;
  }

  private getLegacyStatus(dto: PolarisApiMessageDto): string | undefined {
    return (dto as PolarisApiMessageDto & {status?: string}).status;
  }

  private getLegacyPolarisLambdaRequestId(dto: PolarisApiMessageDto): string | null {
    return (dto as PolarisApiMessageDto & {polarisLambdaRequestId?: string | null}).polarisLambdaRequestId ?? null;
  }

  private toInsights(input: PolarisApiMessageDto['insights'] | string | undefined): string[] | null {
    if (Array.isArray(input)) {
      return input.filter(item => typeof item === 'string');
    }
    if (typeof input === 'string' && input.trim()) {
      return [input];
    }
    return null;
  }

  private toStringArray(input: unknown): string[] {
    if (!Array.isArray(input)) {
      return [];
    }
    return input.filter(item => typeof item === 'string');
  }

  private toDomainCompetitorsKeys(keys: PolarisApiMessageDto['competitorsKeys']): string[] | undefined {
    if (!keys) {
      return undefined;
    }
    if (Array.isArray(keys)) {
      return keys.filter(item => typeof item === 'string');
    }

    const trimmed = keys.trim();
    if (!trimmed) {
      return undefined;
    }
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.filter(item => typeof item === 'string');
        }
      } catch {
        // Fall back to comma-delimited parsing.
      }
    }

    const fromCsv = trimmed
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);

    return fromCsv.length ? fromCsv : undefined;
  }

  private ensureCompetitorsKeysArray(value: unknown): string[] | undefined {
    if (value == null) {
      return undefined;
    }
    if (Array.isArray(value)) {
      const filtered = value.filter((item): item is string => typeof item === 'string');
      return filtered.length ? filtered : undefined;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return undefined;
      }
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            const arr = parsed.filter((item): item is string => typeof item === 'string');
            return arr.length ? arr : undefined;
          }
        } catch {
          // Fall through to comma-delimited parsing.
        }
      }
      const fromCsv = trimmed
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
      return fromCsv.length ? fromCsv : undefined;
    }
    return undefined;
  }

  private toApiCompetitorsKeys(keys: PolarisSendMessageInput['message']['competitorsKeys']): string[] | undefined {
    return this.ensureCompetitorsKeysArray(keys);
  }

  private toDomainOrigin(origin: string | undefined): PolarisTurn['origin'] {
    return toPolarisOrigin(origin);
  }

  private normalizeTimestamp(value: unknown, fallback: number): number;
  private normalizeTimestamp(value: unknown, fallback: null): number | null;
  private normalizeTimestamp(value: unknown, fallback: number | null): number | null {
    if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) {
      return fallback;
    }
    return value;
  }
}
